import React, { Fragment, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import Icon from '../../Icon';
import Loader from '../../common/Loader';
import ScheduleFilter from './ScheduleFilter';
import TimeSlots from '../Components/TimeSlots';
import DayPicker from '../Components/DayPicker';
import MetaHelmet from '../../AppHelmet/MetaHelmet';
import ProviderCard from '../Components/ProviderCard';
import LeaveFlowModal from '../Components/LeaveFlowModal';
import EmptyContent from '../../common/EmptyState/Content';
import AppointmentHeader from '../Components/AppointmentHeader';

import { renderAddress } from '../helpers/renderAddress';
import { usePageView } from '../../../hocs/usePageView';

import { enhance } from './enhance';

import { appointmentProgressActions } from '../../../redux/appointmentForm/progress-types';

import styles from './styles.styl';

function AppointmentProviders({
  activeDate,
  activeLocations,
  clearProgressConsents,
  fetchLocations,
  fetchProviderList,
  filterList,
  formatMessage,
  isEirVisitType,
  isOfficeVisit,
  isVirtualVisit,
  loading,
  locationList,
  scheduleMeta,
  setDateFilter,
  setLocationFilter,
  setScheduleData,
  subtitle,
}) {
  const dispatch = useDispatch();

  const confirmModalRef = useRef();

  usePageView('Get Care | Available Providers');

  const showAddress = isOfficeVisit || isEirVisitType;

  const handleTimeSelect = (time, provider, location) => {
    setScheduleData({ ...scheduleMeta, provider, time, location });

    dispatch(
      appointmentProgressActions.determineNextStepAfterAppointmentProviders(),
    );
  };

  const handleLocationFilter = locations => {
    setLocationFilter(locations);
    fetchProviderList();
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (activeDate) {
      fetchProviderList();
    }
  }, [activeDate]);

  return (
    <Fragment>
      <MetaHelmet title="pages.title.getCare.providers" />
      <AppointmentHeader
        title={
          isEirVisitType
            ? 'appointment.actions.selectYourSlot'
            : 'appointments.form.availableProviders'
        }
        subtitle={subtitle}
        handleBack={() => confirmModalRef.current.open()}
      >
        <ScheduleFilter
          activeDate={activeDate}
          activeLocations={activeLocations}
          disabled={loading}
          filterList={filterList}
          formatMessage={formatMessage}
          isEirVisitType={isEirVisitType}
          isVirtualVisit={isVirtualVisit}
          onDateChange={setDateFilter}
          onLocationFilterApply={handleLocationFilter}
        />
      </AppointmentHeader>
      <DayPicker
        activeDate={activeDate}
        onChange={setDateFilter}
        disabled={loading}
      />
      <div>{loading && <Loader />}</div>
      {!loading &&
        locationList.map(location => {
          const { id, providers = [], timeSlots = [] } = location || {};
          return (
            <Fragment key={id}>
              <div className={styles.locationInfo}>
                <div className={styles.locationName}>
                  <Icon name="pin-location-fill-black" size={12} />
                  {location.name}
                </div>
                {showAddress && (
                  <div className={styles.address}>
                    {renderAddress(location)}
                  </div>
                )}
              </div>
              <div>
                {providers.map(provider => (
                  <ProviderCard
                    onTimeSelect={(time, providerEntity) =>
                      handleTimeSelect(time, providerEntity, location)
                    }
                    provider={provider}
                    key={provider.id}
                    locationId={id}
                  />
                ))}
              </div>
              {isEirVisitType && (
                <div className={styles.eirVisitTimeSlots}>
                  <div className={styles.eirVisitTimeSlots_separator} />
                  <TimeSlots
                    list={timeSlots}
                    onSelect={(time, providerEntity) =>
                      handleTimeSelect(time, providerEntity, location)
                    }
                  />
                </div>
              )}
            </Fragment>
          );
        })}
      {!loading && !locationList.length && (
        <EmptyContent
          styleName="empty-block"
          text={formatMessage({
            id: isEirVisitType
              ? 'appointments.emptySlots'
              : 'appointments.emptyProviders',
          })}
          icon={isEirVisitType ? 'search-list' : 'no-one-found'}
        />
      )}
      <LeaveFlowModal
        ref={confirmModalRef}
        formatMessage={formatMessage}
        clearProgressConsents={clearProgressConsents}
      />
    </Fragment>
  );
}

AppointmentProviders.propTypes = {
  activeDate: PropTypes.string,
  activeLocations: PropTypes.array,
  clearProgressConsents: PropTypes.func,
  fetchLocations: PropTypes.func,
  fetchProviderList: PropTypes.func,
  filterList: PropTypes.array,
  formatMessage: PropTypes.func,
  isEirVisitType: PropTypes.bool,
  isOfficeVisit: PropTypes.bool,
  isVirtualVisit: PropTypes.bool,
  loading: PropTypes.bool,
  locationList: PropTypes.array,
  scheduleMeta: PropTypes.object,
  setDateFilter: PropTypes.func,
  setLocationFilter: PropTypes.func,
  setScheduleData: PropTypes.func,
  subtitle: PropTypes.object,
};

export default enhance(AppointmentProviders);



// WEBPACK FOOTER //
// ./src/components/AppointmentForm/AppointmentProviders/index.jsx